import {
  AlertOctagon,
  ArrowLeftRight,
  Coins,
  Gauge,
  Wallet,
  BadgeIndianRupee,
  Crown,
  UserCheck,
  BellRing,
  ClipboardCheck,
  CreditCard,
  Dumbbell,
  LayoutDashboard,
  LifeBuoy,
  Mail,
  Megaphone,
  ScrollText,
  Send,
  ShieldCheck,
  SlidersHorizontal,
  Tag,
  TicketPercent,
  UserCog,
  Users,
  type LucideIcon,
  Trash2,
} from 'lucide-react';
import { Role } from '@/types/domain';

// -----------------------------------------------------------------------------
// The navigation tree.
//
// One declaration, consumed by the sidebar, the breadcrumbs and the route
// guards. `minimumRole` here drives what the sidebar SHOWS; the route guard
// reads the same field to decide what it lets through.
//
// The sidebar hiding an item is a courtesy, not a control. The API is the thing
// that says no.
// -----------------------------------------------------------------------------

export type NavItem = {
  label: string;
  to: string;
  icon: LucideIcon;
  /**
   * One line explaining what the screen is for.
   *
   * Lives HERE, not in the screen, because the top bar renders it. Screens used
   * to open with a large `<h1>` and a paragraph repeating what the sidebar had
   * already said - three restatements of "Members" above the actual data. The
   * title moved into the header where it is stated once, and the description
   * came with it so the two cannot drift apart.
   */
  description: string;
  /** Omitted means "any staff role". */
  minimumRole?: Role;
  /** Shows a count chip - pending applications, unreviewed submissions, open tickets. */
  badgeKey?: 'pendingApplications' | 'reviewQueue' | 'openTickets';
};

/**
 * A nav row that expands instead of navigating.
 *
 * Exists because Messaging is one CONCERN with several surfaces - compose a
 * push, compose an email, look at what was sent - and three sibling rows in the
 * rail claimed the visual weight of three unrelated destinations. Nesting them
 * says "these belong together" in the structure rather than in a heading, which
 * is the same reason the group headings came out.
 *
 * Deliberately NOT navigable itself. A parent row that both expands and goes
 * somewhere is a coin toss every time you click it.
 */
export type NavGroup = {
  label: string;
  icon: LucideIcon;
  children: NavItem[];
  /** Applies to the whole group; children may narrow it further. */
  minimumRole?: Role;
};

export type NavEntry = NavItem | NavGroup;

export function isNavGroup(entry: NavEntry): entry is NavGroup {
  return 'children' in entry;
}

export type NavSection = {
  /** Names the band for the top bar's breadcrumb. Not rendered in the rail. */
  label: string;
  items: NavEntry[];
};

export const NAV_SECTIONS: NavSection[] = [
  {
    label: 'Overview',
    items: [
      {
        label: 'Dashboard',
        to: '/',
        icon: LayoutDashboard,
        description: 'Platform health and everything currently waiting on a decision.',
      },
    ],
  },
  {
    label: 'Moderation',
    items: [
      {
        label: 'Coach Applications',
        to: '/applications',
        icon: ClipboardCheck,
        description: 'Oldest first. Approving a coach publishes them to every member.',
        badgeKey: 'pendingApplications',
      },
      {
        label: 'Review Queue',
        to: '/review-queue',
        icon: ShieldCheck,
        description: 'Certificates, transformations, media and profiles in one inbox.',
        badgeKey: 'reviewQueue',
      },
    ],
  },
  {
    label: 'People',
    items: [
      {
        label: 'Members',
        to: '/members',
        icon: Users,
        description: 'Contact details are masked. Health data is never shown here.',
      },
      {
        label: 'Coaches',
        to: '/coaches',
        icon: Dumbbell,
        description: 'Every coach account, approved and otherwise.',
      },
      {
        label: 'Deletions',
        to: '/deletions',
        icon: Trash2,
        description:
          'Accounts their owners asked us to delete. Already closed, and erased automatically once the retention window ends.',
      },
      {
        label: 'Staff',
        to: '/staff',
        icon: UserCog,
        description: 'Who can sign in to this panel, and what they may do.',
        minimumRole: Role.SUPER_ADMIN,
      },
    ],
  },
  {
    label: 'Commerce',
    items: [
      {
        label: 'Plans',
        to: '/plans',
        icon: Tag,
        description: "What the app charges for its own plans. Changing a price needs a super admin.",
      },
      {
        label: 'Discount codes',
        to: '/coupons',
        icon: TicketPercent,
        description: 'Codes members can redeem at checkout. Creating one needs a super admin.',
      },
      {
        label: 'Subscriptions',
        to: '/subscriptions',
        icon: BadgeIndianRupee,
        description: 'Read-only ledger of every subscription on the platform.',
      },
      {
        label: 'Payments',
        to: '/payments',
        icon: CreditCard,
        description: 'Every transaction. Refunds are coming soon.',
      },
    ],
  },
  {
    label: 'Memberships',
    items: [
      {
        // Members AND the plan catalogue, as two tabs. The catalogue was a
        // separate "Membership settings" row; two rows for one product split
        // what is really one screen.
        label: 'Memberships',
        to: '/memberships',
        icon: Crown,
        description: 'Who holds Advanced or Pro, and the plans themselves - price, trial and whether they are on sale.',
      },
      {
        label: 'Coach assignments',
        to: '/memberships/coach-assignments',
        icon: UserCheck,
        description: 'Pro members waiting for a Sect.fit coach, oldest SLA first. Target: 48 hours.',
      },
      {
        label: 'Billing anomalies',
        to: '/memberships/anomalies',
        icon: AlertOctagon,
        description: 'Double-billing guards and failed Razorpay commands that need a person.',
      },
    ],
  },
  {
    // Sect Coins - members' in-app reward currency. NOT the coach's
    // real-money TrainerWallet, which has no admin surface here.
    label: 'Wallet & Economy',
    items: [
      {
        label: 'Wallet & Economy',
        icon: Coins,
        children: [
          {
            label: 'Overview',
            to: '/wallet',
            icon: Gauge,
            description: 'Coins in circulation, earned and spent, and the rules currently live.',
          },
          {
            label: 'Member wallets',
            to: '/wallet/members',
            icon: Wallet,
            description: 'Balances, levels and full coin history per member. Corrections are audited and shown to the member.',
          },
          {
            label: 'Coin transactions',
            to: '/wallet/transactions',
            icon: ArrowLeftRight,
            description: 'Every earn, spend and correction across all members, newest first.',
          },
          {
            label: 'Economy settings',
            to: '/wallet/settings',
            icon: SlidersHorizontal,
            description: 'Rewards, caps, levels, scanner limits and the extra-scan price. Changes need a super admin.',
          },
        ],
      },
    ],
  },
  {
    label: 'Messaging',
    items: [
      {
        label: 'Messaging',
        icon: Megaphone,
        children: [
          {
            label: 'Push Notifications',
            to: '/messaging/push',
            icon: BellRing,
            description: "Goes to the phone. Also lands in the app's notification list.",
          },
          {
            label: 'Email',
            to: '/messaging/email',
            icon: Mail,
            description: 'Sent from the project mailbox to everyone with an address on file.',
          },
          {
            label: 'Sent History',
            to: '/messaging/history',
            icon: Send,
            description: 'Every broadcast, who sent it, and how many it reached.',
          },
        ],
      },
    ],
  },
  {
    label: 'Support',
    items: [
      {
        label: 'Tickets',
        to: '/tickets',
        icon: LifeBuoy,
        description: 'Everything members and coaches have raised.',
        badgeKey: 'openTickets',
      },
    ],
  },
  {
    label: 'System',
    items: [
      {
        label: 'Configuration',
        to: '/configuration',
        icon: SlidersHorizontal,
        description: 'Read by every mobile client on launch. Changes apply immediately.',
        minimumRole: Role.SUPER_ADMIN,
      },
      {
        label: 'Audit Log',
        to: '/audit-log',
        icon: ScrollText,
        description: 'Every staff action, immutable. Cannot be edited or deleted.',
        minimumRole: Role.SUPER_ADMIN,
      },
    ],
  },
];

/** Every leaf, groups flattened. The one place nesting is unwrapped. */
export const NAV_LEAVES: NavItem[] = NAV_SECTIONS.flatMap((section) =>
  section.items.flatMap((entry) => (isNavGroup(entry) ? entry.children : [entry])),
);

/**
 * Leaves whose path is a prefix of another leaf - `/`, `/memberships`, `/wallet`.
 *
 * Their nav row must match EXACTLY. With prefix matching, opening
 * `/wallet/members` lit both "Overview" (`/wallet`) and "Member wallets", so
 * the rail showed two current pages at once.
 */
export const NAV_EXACT_PATHS: ReadonlySet<string> = new Set(
  NAV_LEAVES.filter((item) =>
    NAV_LEAVES.some((other) => other !== item && other.to.startsWith(item.to === '/' ? '/' : `${item.to}/`)),
  ).map((item) => item.to),
);

/** Flat lookup for breadcrumbs and the document title. */
export const NAV_ITEMS_BY_PATH: Record<string, NavItem> = Object.fromEntries(
  NAV_LEAVES.map((item) => [item.to, item]),
);

/**
 * Which group a route belongs to.
 *
 * The top bar renders it as the first crumb, so a screen states where it sits in
 * the tree without the sidebar having to be visible - which is the whole
 * difference between a usable and an unusable panel on a phone, where the rail
 * is behind a button.
 */
export const NAV_SECTION_BY_PATH: Record<string, string> = Object.fromEntries(
  NAV_SECTIONS.flatMap((section) =>
    section.items.flatMap((entry) =>
      isNavGroup(entry)
        ? // A child crumbs to its GROUP, which is the thing the reader sees in
          // the rail. Crumbing to the section instead would name a band that is
          // not rendered anywhere.
          entry.children.map((child) => [child.to, entry.label])
        : [[entry.to, section.label]],
    ),
  ),
);
